/* eslint-disable */
import React, { useState, useEffect } from "react";
import { db } from "../../firebase-config";
import { useParams } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import "./GeneratedPatientData.css";
import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCube, faFileAlt, faPrescription } from "@fortawesome/free-solid-svg-icons";
import {
  faUser,
  faIdBadge,
  faVenusMars,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Button, Modal } from "react-bootstrap";

// Decrypt functions
const decrypt = (data) => window.atob(data);
const encryptData = (data) => window.btoa(data);

const decryptData = (encryptedData) => {
  const secretKey = process.env.REACT_APP_SECRET_KEY || "default_key";
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedData) throw new Error("Decryption failed");
    return decryptedData;
  } catch (error) {
    return encryptedData;
  }
};

// Helper function to decrypt value if it's encrypted, otherwise return as-is
const decryptIfNeeded = (value) => {
  // Return null/undefined as-is to preserve emptiness check
  if (value === null || value === undefined) return null;
  
  // Return empty string as null to make conditionals work
  if (value === "") return null;
  
  // If it's an array, decrypt each item if needed
  if (Array.isArray(value)) {
    // Filter out empty values and decrypt the rest
    const processed = value
      .filter(item => item !== "" && item !== null && item !== undefined)
      .map(item => decryptIfNeeded(item));
    return processed.length > 0 ? processed : null;
  }
  
  // Check if value looks like it's encrypted (starts with U2FsdGVk which is base64 for Salted__)
  if (typeof value === 'string' && value.startsWith('U2FsdGVk')) {
    const decrypted = decryptData(value);
    return decrypted || null;
  }
  
  // Return plain text value as-is
  return value;
};

// Accept the hideShare prop (default = false).
const GeneratedPatientData = ({ hideShare = false }) => {
  const { adminName, patientId } = useParams();
  const decryptedAdminName = decrypt(adminName);
  const decryptedPatientId = decrypt(patientId);

  const [patientData, setPatientData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal state to show/hide and store link
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedLink, setSelectedLink] = useState("");

  // Fetch patient data
  useEffect(() => {
    setLoading(true);
    const patientRef = ref(
      db,
      `superadmin/admins/${decryptedAdminName}/patients/${decryptedPatientId}`
    );

    onValue(patientRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        console.log("Raw patient data:", data);
        console.log("Uploads before processing:", data.uploads);
        
        // Process uploads - check for Scan 1, Scan 2, etc.
        let uploads = [];
        if (data.uploads && typeof data.uploads === "object") {
          // Check for Scan 1, Scan 2 format
          const scanKeys = Object.keys(data.uploads).filter(key => key.startsWith('Scan'));
          if (scanKeys.length > 0) {
            // Process each scan
            uploads = scanKeys.map(scanKey => {
              const scan = data.uploads[scanKey];
              return {
                // Decrypt values if they are encrypted, otherwise use as-is
                FolderPath: decryptIfNeeded(scan.FolderPath),
                Modality: decryptIfNeeded(scan.Modality),
                StudyDate: decryptIfNeeded(scan.StudyDate),
                Type_of_Data: decryptIfNeeded(scan.Type_of_Dicom_Data || scan.Type_of_Data),
                Type_of_CT: decryptIfNeeded(scan.Type_of_CT),
                Type_of_Dicom_Data: decryptIfNeeded(scan.Type_of_Dicom_Data),
                Dicom_3D: decryptIfNeeded(scan.Dicom_3D),
                Dicom_3DURL: decryptIfNeeded(scan.Dicom_3DURL),
                Dicom_Report: decryptIfNeeded(scan.Dicom_Report),
                // Check for ReportURL in scan first, then fallback to patient level
                Dicom_ReportURL: decryptIfNeeded(scan.Dicom_ReportURL) || decryptIfNeeded(data.Dicom_ReportURL),
                Path3D: decryptIfNeeded(scan.Path3D),
                PathReport: decryptIfNeeded(scan.PathReport),
                Prescription: decryptIfNeeded(scan.Prescription),
                PrescriptionURL: decryptIfNeeded(scan.PrescriptionURL),
                PathPrescription: decryptIfNeeded(scan.PathPrescription),
              };
            });
          } else if (Array.isArray(data.uploads)) {
            // Handle array format
            uploads = data.uploads.map(upload => ({
              FolderPath: decryptIfNeeded(upload.FolderPath),
              Modality: decryptIfNeeded(upload.Modality),
              StudyDate: decryptIfNeeded(upload.StudyDate),
              Type_of_Data: decryptIfNeeded(upload.Type_of_Dicom_Data || upload.Type_of_Data),
              Type_of_CT: decryptIfNeeded(upload.Type_of_CT),
              Type_of_Dicom_Data: decryptIfNeeded(upload.Type_of_Dicom_Data),
              Dicom_3D: decryptIfNeeded(upload.Dicom_3D),
              Dicom_3DURL: decryptIfNeeded(upload.Dicom_3DURL),
              Dicom_Report: decryptIfNeeded(upload.Dicom_Report),
              // Check for ReportURL in upload first, then fallback to patient level
              Dicom_ReportURL: decryptIfNeeded(upload.Dicom_ReportURL) || decryptIfNeeded(data.Dicom_ReportURL),
              Path3D: decryptIfNeeded(upload.Path3D),
              PathReport: decryptIfNeeded(upload.PathReport),
              Prescription: decryptIfNeeded(upload.Prescription),
              PrescriptionURL: decryptIfNeeded(upload.PrescriptionURL),
              PathPrescription: decryptIfNeeded(upload.PathPrescription),
            }));
          } else {
            uploads = Object.values(data.uploads);
          }
        }

        // If no uploads found in uploads node, check main patient node
        if (uploads.length === 0) {
          // Create a single upload entry from patient-level data
          uploads = [{
            FolderPath: decryptIfNeeded(data.FolderPath),
            Modality: decryptIfNeeded(data.Modality),
            StudyDate: decryptIfNeeded(data.StudyDate),
            Type_of_Data: decryptIfNeeded(data.Type_of_Dicom_Data || data.Type_of_Data),
            Type_of_CT: decryptIfNeeded(data.Type_of_CT),
            Type_of_Dicom_Data: decryptIfNeeded(data.Type_of_Dicom_Data),
            Dicom_3D: decryptIfNeeded(data.Dicom_3D),
            Dicom_3DURL: decryptIfNeeded(data.Dicom_3DURL),
            Dicom_Report: decryptIfNeeded(data.Dicom_Report),
            Dicom_ReportURL: decryptIfNeeded(data.Dicom_ReportURL),
            Path3D: decryptIfNeeded(data.Path3D),
            PathReport: decryptIfNeeded(data.PathReport),
            Prescription: decryptIfNeeded(data.Prescription),
            PrescriptionURL: decryptIfNeeded(data.PrescriptionURL),
            PathPrescription: decryptIfNeeded(data.PathPrescription),
          }];
        }
        
        // If uploads exist but Report URL is at patient level, add it to uploads
        if (uploads.length > 0 && data.Dicom_ReportURL && uploads[0] && !uploads[0].Dicom_ReportURL) {
          uploads = uploads.map(upload => ({
            ...upload,
            Dicom_ReportURL: upload.Dicom_ReportURL || decryptIfNeeded(data.Dicom_ReportURL)
          }));
        }

        // Get basic patient info - decrypt values if encrypted
        const decryptedData = {
          ...data,
          FullName: decryptIfNeeded(data.FullName) || "NA",
          Age: decryptIfNeeded(data.Age) || "NA",
          Gender: decryptIfNeeded(data.Gender) || "NA",
          ID: decryptIfNeeded(data.ID) || "NA",
          Clinical_Textarea: decryptIfNeeded(data.Clinical_Textarea),
          Clinical_Filepath: decryptIfNeeded(data.Clinical_Filepath),
          Clinical_Timestamp: decryptIfNeeded(data.Clinical_Timestamp),
          uploads: uploads,
        };

        console.log("Processed uploads:", decryptedData.uploads);
        console.log("Final decrypted patient data:", decryptedData);
        setPatientData(decryptedData);
      } else {
        setPatientData(null);
      }
      setLoading(false);
    });
  }, [decryptedAdminName, decryptedPatientId]);

  const handleShareClick = () => {
    const baseUrl = window.location.origin;
  
    // OLD (shows share button): 
    // const generatedLink = `${baseUrl}/patient/${encryptData(decryptedAdminName)}/${encryptData(decryptedPatientId)}`;
  
    // NEW (hide share button):
    const generatedLink = `${baseUrl}/shared/patient/${encryptData(
      decryptedAdminName
    )}/${encryptData(decryptedPatientId)}`;
  
    setSelectedLink(generatedLink);
    setModalVisible(true);
  };

  const copyToClipboard = () => {
    const baseUrl = window.location.origin;
  
    // Ensure the generated link is created here
    const generatedLink = `${baseUrl}/shared/patient/${encryptData(decryptedAdminName)}/${encryptData(decryptedPatientId)}`;
  
    navigator.clipboard.writeText(generatedLink).then(() => {
      toast.success("Link copied to clipboard!", { position: "top-center" });
    }).catch((err) => {
      toast.error("Failed to copy link", { position: "top-center" });
      console.error("Copy failed:", err);
    });
  };

  const shareViaWhatsApp = () => {
    const baseUrl = window.location.origin;
  
    // Ensure the generated link is created here
    const generatedLink = `${baseUrl}/patient/${encryptData(decryptedAdminName)}/${encryptData(decryptedPatientId)}`;
  
    // Encode the full message with the link
    const whatsappMessage = `Check this patient's data: ${generatedLink}`;
  
    // Open WhatsApp with the message
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, "_blank");
  };

  const renderTableRows = () => {
    if (patientData?.uploads?.length > 0) {
      return patientData.uploads.map((upload, index) => (
        <tr key={index}>
          <td>{upload.StudyDate || "No Date Available"}</td>
          <td>
            {upload.FolderPath ? (
              <a
                href={`https://tryon.easiofy.com/DWV/?Patient=${encodeURIComponent(
                  upload.FolderPath
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {upload.Modality || "SCAN"}
              </a>
            ) : (
              upload.Modality || "N/A"
            )}
          </td>
          <td>
            {(() => {
              const typeOfCT = upload.Type_of_CT || null;
              const typeOfDICOMData = upload.Type_of_Dicom_Data || null;
              const typeOfData = upload.Type_of_Data || null;
              return typeOfCT || typeOfDICOMData || typeOfData || "N/A";
            })()}
          </td>
          {/* <td>
            {upload.Dicom_3DURL && upload.Dicom_3DURL !== "Not Uploaded" && upload.Dicom_3DURL !== "" ? (
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  color: "#0056b3",
                }}
                onClick={() => {
                  const decrypted3DURL = upload.Dicom_3DURL; // Already decrypted in data processing
                  if (decrypted3DURL) {
                    const secretKey = process.env.REACT_APP_SECRET_KEY;
                    const encryptedURL = CryptoJS.AES.encrypt(
                      decrypted3DURL,
                      secretKey
                    ).toString();
                   // const modelViewerURL = `https://easiofyviewer.netlify.app/?src=${encodeURIComponent(
                      
                    const modelViewerURL = `https://health.easiofy.com/3dViewer/?src=${encodeURIComponent(
                    encryptedURL
                    )}`;
                    window.open(modelViewerURL, "_blank");
                  } else {
                    toast.info("3D view is not available", {
                      position: "top-center",
                    });
                  }
                }}
              >
                Available
              </button>
            ) : upload.Dicom_3D ? (
              <span style={{ color: "#28a745" }}>Available</span>
            ) : (
              "N/A"
            )}
          </td> */}
          <td>
            {upload.Dicom_ReportURL && upload.Dicom_ReportURL !== "Not Uploaded" && upload.Dicom_ReportURL !== "" ? (
              <a
                href={upload.Dicom_ReportURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#0056b3" }}
              >
                Available
              </a>
            ) : upload.Dicom_Report ? (
              <span style={{ color: "#28a745" }}>Available</span>
            ) : (
              "N/A"
            )}
          </td>
          <td>
            {upload.PrescriptionURL && upload.PrescriptionURL.length > 0 ? (
              Array.isArray(upload.PrescriptionURL) ? (
                upload.PrescriptionURL.map((url, i) => (
                  <a
                    key={i}
                    href={url} // Already decrypted in data processing
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      marginRight: "10px",
                      color: "#0056b3",
                      textDecoration: "none",
                    }}
                  >
                    {upload.Prescription && upload.Prescription[i] ? upload.Prescription[i] : `RX ${i + 1}`}
                  </a>
                ))
              ) : (
                <a
                  href={upload.PrescriptionURL} // Already decrypted in data processing
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#0056b3",
                    textDecoration: "none",
                  }}
                >
                  {upload.Prescription || "RX 1"}
                </a>
              )
            ) : upload.Prescription && upload.Prescription.length > 0 ? (
              <span style={{ color: "#28a745" }}>Available</span>
            ) : (
              "N/A"
            )}
          </td>
        </tr>
      ));
    }

    // If older single-record data structure:
    return (
      <tr>
        <td>{patientData.StudyDate || "No Date Available"}</td>
        <td>
          {patientData.FolderPath ? (
            <a
              href={`https://tryon.easiofy.com/DWV/?Patient=${encodeURIComponent(
                patientData.FolderPath || ""
              )}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {patientData.Modality || "SCAN"}
            </a>
          ) : (
            patientData.Modality || "N/A"
          )}
        </td>
        <td>
          {(() => {
            const typeOfCT = patientData.Type_of_CT || null;
            const typeOfDICOMData = patientData.Type_of_Dicom_Data || null;
            const typeOfData = patientData.Type_of_Data || null;
            return typeOfCT || typeOfDICOMData || typeOfData || "N/A";
          })()}
        </td>
        {/* <td>
          {patientData.Dicom_3DURL && patientData.Dicom_3DURL !== "Not Uploaded" && patientData.Dicom_3DURL !== "" ? (
            <button
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                color: "#0056b3",
              }}
              onClick={() => {
                const decrypted3DURL = patientData.Dicom_3DURL; // Already decrypted in data processing
                if (decrypted3DURL) {
                  const secretKey = process.env.REACT_APP_SECRET_KEY;
                  const encryptedURL = CryptoJS.AES.encrypt(
                    decrypted3DURL,
                    secretKey
                  ).toString();
                  const modelViewerURL = `https://health.easiofy.com/3dViewer/?src=${encodeURIComponent(
                    encryptedURL
                  )}`;
                  window.open(modelViewerURL, "_blank");
                } else {
                  toast.info("3D view is not available", {
                    position: "top-center",
                  });
                }
              }}
            >
              Available
            </button>
          ) : patientData.Dicom_3D ? (
            <span style={{ color: "#28a745" }}>Available</span>
          ) : (
            "N/A"
          )}
        </td> */}
        <td>
          {patientData.Dicom_ReportURL && patientData.Dicom_ReportURL !== "Not Uploaded" && patientData.Dicom_ReportURL !== "" ? (
            <a
              href={patientData.Dicom_ReportURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Available
            </a>
          ) : patientData.Dicom_Report ? (
            <span style={{ color: "#28a745" }}>Available</span>
          ) : (
            "N/A"
          )}
        </td>
        <td>
          {patientData.PrescriptionURL && patientData.PrescriptionURL !== "Not Uploaded" ? (
            <a
              href={patientData.PrescriptionURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faPrescription} title="Prescription" />
            </a>
          ) : (
            "Not Uploaded"
          )}
        </td>
      </tr>
    );
  };

  return (
    <>
      <div className="header">
        <h1
          className="page-title"
          style={{ textAlign: "center", textDecoration: "underline", color: "#005b96" }}
        >
          Patient Medical Records
        </h1>
      </div>

{!hideShare && (
  <div className="share">
    <button className="btn-copy-link" onClick={copyToClipboard}>
   Link
    </button>
    <button className="btn-whatsapp" onClick={shareViaWhatsApp}>
      WhatsApp
    </button>
  </div>
)}

      {loading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
          <p>Please wait...</p>
        </div>
      )}

      {patientData ? (
        <div className="containergenrate">
          {/* Patient Information Section */}
          <div className="section patient-info">
            <div className="patient-details">
              <div className="detail-item">
                <FontAwesomeIcon icon={faUser} className="detail-icon" />
                <div className="detail-content">
                  <label> Name:</label>
                  <span>{patientData.FullName || "NA"}</span>
                </div>
              </div>
              <div className="detail-item">
                <FontAwesomeIcon icon={faIdBadge} className="detail-icon" />
                <div className="detail-content">
                  <label>ID:</label>
                  <span>{patientData.ID || "NA"}</span>
                </div>
              </div>
              <div className="detail-item">
                <FontAwesomeIcon icon={faCalendarAlt} className="detail-icon" />
                <div className="detail-content">
                  <label>Age:</label>
                  <span>
                    {patientData.Age ? `${patientData.Age}` : "NA"}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <FontAwesomeIcon icon={faVenusMars} className="detail-icon" />
                <div className="detail-content">
                  <label>Gender:</label>
                  <span>{patientData.Gender || "NA"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Clinical History Section */}
          <div className="section clinical-history">
            <div className="section-title">Clinical History</div>
            <div className="clinical-history-content">
              {patientData.Clinical_Textarea ? (
                Array.isArray(patientData.Clinical_Textarea) ? (
                  patientData.Clinical_Textarea.map((entry, index) => (
                    <div key={index} className="clinical-entry">
                      <strong>Entry {index + 1}:</strong>
                      <p>{entry}</p>
                    </div>
                  ))
                ) : (
                  <p>{patientData.Clinical_Textarea}</p>
                )
              ) : (
                <p>No Clinical History Available</p>
              )}

              {patientData.Clinical_Filepath ? (
                Array.isArray(patientData.Clinical_Filepath) ? (
                  patientData.Clinical_Filepath.map((url, index) => (
                    <div key={index}>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        View File {index + 1}
                      </a>
                    </div>
                  ))
                ) : (
                  <a
                    href={patientData.Clinical_Filepath}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View File
                  </a>
                )
              ) : null}
            </div>
          </div>

          <div className="section patient-data-section">
            <h2 className="section-title">Patient Data Details</h2>
            <div className="medical-records-table-wrapper">
              <table className="medical-records-compact-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Scans</th>
                    <th>Category</th>
                    {/* <th>3D</th> */}
                    <th>Reports</th>
                    <th>Prescriptions</th>
                  </tr>
                </thead>
                <tbody>{renderTableRows()}</tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <p>No patient data found.</p>
      )}

      {/* Modal for Sharing Options */}
      <Modal show={modalVisible} onHide={() => setModalVisible(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Share Patient Link</Modal.Title>
        </Modal.Header>
        {/* <Modal.Body>
          <p style={{ wordBreak: "break-all" }}>{selectedLink}</p>
        </Modal.Body> */}
        <Modal.Footer>
          <Button variant="primary" onClick={copyToClipboard}>
            Copy Link
          </Button>
          <Button variant="success" onClick={shareViaWhatsApp}>
            WhatsApp
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GeneratedPatientData;